<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import ContactAPI from 'dashboard/api/contacts';
import { isLabelInCategory } from 'dashboard/constants/labelCategories';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const emit = defineEmits(['save', 'remove']);

const { t } = useI18n();
const store = useStore();
const agents = useMapGetter('agents/getAgents');
const labels = useMapGetter('labels/getLabels');

// Labels assignable to calendar events (global or calendar_event-categorised),
// plus any already selected on this event so they stay visible/removable.
const availableLabels = computed(() =>
  (labels.value || []).filter(
    label =>
      isLabelInCategory(label, 'calendar_event') ||
      form.labels.includes(label.title)
  )
);

const dialogRef = ref(null);
const editingId = ref(null);
const editingPublicId = ref(null);

const emptyForm = () => ({
  title: '',
  date: '',
  startTime: '09:00',
  endTime: '09:30',
  description: '',
  agentIds: [],
  contacts: [], // [{ id, name }]
  labels: [], // [title]
  reminderEnabled: false,
  reminderOffset: 0, // minutes before start time
});

// Lead-time options for the auto-created reminder.
const REMINDER_OFFSET_KEYS = [
  { value: 0, key: 'AT_START' },
  { value: 5, key: 'MIN_5' },
  { value: 10, key: 'MIN_10' },
  { value: 15, key: 'MIN_15' },
  { value: 30, key: 'MIN_30' },
  { value: 60, key: 'HOUR_1' },
  { value: 1440, key: 'DAY_1' },
];
const REMINDER_OFFSETS = computed(() =>
  REMINDER_OFFSET_KEYS.map(({ value, key }) => ({
    value,
    label: t(`CALENDAR.DIALOG.REMINDER.OFFSETS.${key}`),
  }))
);

const form = reactive(emptyForm());

const isEditing = computed(() => Boolean(editingId.value));
// Times are zero-padded 'HH:mm' on the same day, so string compare is chronological.
const isTimeInvalid = computed(
  () =>
    Boolean(form.startTime) &&
    Boolean(form.endTime) &&
    form.endTime <= form.startTime
);
// New events can't start in the past; editing existing (possibly past) events is allowed.
const isStartInPast = computed(
  () =>
    !isEditing.value &&
    Boolean(form.date) &&
    Boolean(form.startTime) &&
    new Date(`${form.date}T${form.startTime}`) < new Date()
);
const isFormInvalid = computed(
  () =>
    !form.title.trim() ||
    !form.date ||
    isTimeInvalid.value ||
    isStartInPast.value
);

const resetForm = () => {
  Object.assign(form, emptyForm());
  editingId.value = null;
  editingPublicId.value = null;
  contactSearch.value = '';
  contactResults.value = [];
};

// --- Agents (Dotchat users) -------------------------------------------------
const toggleAgent = id => {
  form.agentIds = form.agentIds.includes(id)
    ? form.agentIds.filter(a => a !== id)
    : [...form.agentIds, id];
};

// --- Labels (optional) ------------------------------------------------------
const toggleLabel = title => {
  form.labels = form.labels.includes(title)
    ? form.labels.filter(l => l !== title)
    : [...form.labels, title];
};

const labelColor = title =>
  (labels.value || []).find(l => l.title === title)?.color || '#9ca3af';

// --- Contacts (searchable multi-select) -------------------------------------
const contactSearch = ref('');
const contactResults = ref([]);
const isSearching = ref(false);

const searchContacts = async () => {
  const q = contactSearch.value.trim();
  if (!q) {
    contactResults.value = [];
    return;
  }
  isSearching.value = true;
  try {
    const { data } = await ContactAPI.search(q, 1, 'name', '');
    contactResults.value = (data.payload || []).slice(0, 8);
  } catch {
    contactResults.value = [];
  } finally {
    isSearching.value = false;
  }
};

const addContact = contact => {
  if (!form.contacts.some(c => c.id === contact.id)) {
    form.contacts = [...form.contacts, { id: contact.id, name: contact.name }];
  }
  contactSearch.value = '';
  contactResults.value = [];
};

const removeContact = id => {
  form.contacts = form.contacts.filter(c => c.id !== id);
};

// --- Open / submit ----------------------------------------------------------
const openForCreate = (date, contact) => {
  resetForm();
  if (date) form.date = date;
  if (contact) form.contacts = [contact];
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  dialogRef.value?.open();
};

const openForEdit = event => {
  resetForm();
  editingId.value = event.id;
  editingPublicId.value = event.publicId || null;
  Object.assign(form, {
    title: event.title,
    date: event.date,
    startTime: event.startTime || '',
    endTime: event.endTime || '',
    description: event.description || '',
    agentIds: [...(event.agentIds || [])],
    contacts: [...(event.contacts || [])],
    labels: [...(event.labels || [])],
    reminderEnabled: Boolean(event.reminderEnabled),
    reminderOffset: event.reminderOffset ?? 0,
  });
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  dialogRef.value?.open();
};

const handleConfirm = () => {
  if (isFormInvalid.value) return;
  const agentList = (agents.value || []).filter(a =>
    form.agentIds.includes(a.id)
  );
  emit('save', {
    id: editingId.value,
    title: form.title.trim(),
    date: form.date,
    startTime: form.startTime,
    endTime: form.endTime,
    description: form.description.trim(),
    agentIds: [...form.agentIds],
    agents: agentList.map(a => ({ id: a.id, name: a.name })),
    contacts: [...form.contacts],
    contactIds: form.contacts.map(c => c.id),
    labels: [...form.labels],
    color: form.labels.length ? labelColor(form.labels[0]) : null,
    reminderEnabled: form.reminderEnabled,
    reminderOffset: Number(form.reminderOffset) || 0,
  });
};

const handleRemove = () => {
  if (editingId.value) emit('remove', editingId.value);
};

const onSuccess = () => {
  dialogRef.value?.close();
  resetForm();
};

defineExpose({ openForCreate, openForEdit, onSuccess });

const fieldClass =
  'w-full h-9 px-3 rounded-lg text-sm bg-n-alpha-black2 text-n-slate-12 border border-n-weak outline-none focus:border-n-brand';
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-5">
      <span class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
        {{
          isEditing
            ? t('CALENDAR.DIALOG.EDIT_TITLE')
            : t('CALENDAR.DIALOG.CREATE_TITLE')
        }}
        <span
          v-if="editingPublicId"
          class="text-[11px] font-mono px-1.5 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-10"
        >
          {{ editingPublicId }}
        </span>
      </span>

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">
          {{ t('CALENDAR.DIALOG.FIELDS.TITLE') }}
        </span>
        <Input
          v-model="form.title"
          :placeholder="t('CALENDAR.DIALOG.PLACEHOLDERS.TITLE')"
          autofocus
        />
      </label>

      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ t('CALENDAR.DIALOG.FIELDS.DATE') }}
          </span>
          <input v-model="form.date" type="date" :class="fieldClass" />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ t('CALENDAR.DIALOG.FIELDS.START') }}
          </span>
          <input
            v-model="form.startTime"
            type="time"
            :class="[fieldClass, isStartInPast ? '!border-n-ruby-8' : '']"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ t('CALENDAR.DIALOG.FIELDS.END') }}
          </span>
          <input
            v-model="form.endTime"
            type="time"
            :class="[fieldClass, isTimeInvalid ? '!border-n-ruby-8' : '']"
          />
        </label>
      </div>
      <p v-if="isTimeInvalid" class="-mt-3 text-xs text-n-ruby-11">
        {{ t('CALENDAR.DIALOG.VALIDATION.END_BEFORE_START') }}
      </p>
      <p v-if="isStartInPast" class="-mt-3 text-xs text-n-ruby-11">
        {{ t('CALENDAR.DIALOG.VALIDATION.START_IN_PAST') }}
      </p>

      <!-- Agents -->
      <div class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-11">
          {{ t('CALENDAR.DIALOG.FIELDS.AGENTS') }}
        </span>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="agent in agents"
            :key="agent.id"
            type="button"
            class="px-3 py-1.5 rounded-lg text-xs border transition-colors"
            :class="
              form.agentIds.includes(agent.id)
                ? 'bg-n-brand/10 border-n-brand text-n-slate-12'
                : 'bg-n-alpha-black2 border-n-weak text-n-slate-11 hover:border-n-slate-7'
            "
            @click="toggleAgent(agent.id)"
          >
            {{ agent.name }}
          </button>
          <span v-if="!agents.length" class="text-xs text-n-slate-10">
            {{ t('CALENDAR.DIALOG.EMPTY.AGENTS') }}
          </span>
        </div>
      </div>

      <!-- Contacts -->
      <div class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-11">
          {{ t('CALENDAR.DIALOG.FIELDS.CONTACTS') }}
        </span>
        <div class="flex flex-wrap gap-2" v-if="form.contacts.length">
          <span
            v-for="contact in form.contacts"
            :key="contact.id"
            class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-n-solid-3 text-n-slate-12"
          >
            {{ contact.name }}
            <button
              type="button"
              class="i-lucide-x size-3 text-n-slate-10 hover:text-n-slate-12"
              @click="removeContact(contact.id)"
            />
          </span>
        </div>
        <div class="relative">
          <input
            v-model="contactSearch"
            type="text"
            :class="fieldClass"
            :placeholder="t('CALENDAR.DIALOG.PLACEHOLDERS.CONTACT_SEARCH')"
            @input="searchContacts"
          />
          <div
            v-if="contactResults.length"
            class="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-lg"
          >
            <button
              v-for="contact in contactResults"
              :key="contact.id"
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
              @click="addContact(contact)"
            >
              <span class="i-lucide-user-round size-4 text-n-slate-10" />
              {{ contact.name }}
            </button>
          </div>
          <span
            v-else-if="contactSearch && !isSearching"
            class="text-xs text-n-slate-10"
          >
            {{ t('CALENDAR.DIALOG.EMPTY.CONTACT_RESULTS') }}
          </span>
        </div>
      </div>

      <!-- Labels -->
      <div class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-11">
          {{ t('CALENDAR.DIALOG.FIELDS.LABELS') }}
        </span>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="label in availableLabels"
            :key="label.id"
            type="button"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition-colors"
            :class="
              form.labels.includes(label.title)
                ? 'border-n-brand text-n-slate-12 bg-n-brand/10'
                : 'bg-n-alpha-black2 border-n-weak text-n-slate-11 hover:border-n-slate-7'
            "
            @click="toggleLabel(label.title)"
          >
            <span v-if="label.icon">{{ label.icon }}</span>
            <span
              v-else
              class="size-2 rounded-full"
              :style="{ backgroundColor: label.color }"
            />
            {{ label.title }}
          </button>
          <span v-if="!availableLabels.length" class="text-xs text-n-slate-10">
            {{ t('CALENDAR.DIALOG.EMPTY.LABELS') }}
          </span>
        </div>
      </div>

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">
          {{ t('CALENDAR.DIALOG.FIELDS.DESCRIPTION') }}
        </span>
        <TextArea
          v-model="form.description"
          :placeholder="t('CALENDAR.DIALOG.PLACEHOLDERS.DESCRIPTION')"
          :max-length="500"
          class="w-full"
          auto-height
        />
      </label>

      <!-- Reminder for the event start -->
      <div class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak bg-n-alpha-black2">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="form.reminderEnabled"
            type="checkbox"
            class="size-4 accent-n-brand"
          />
          <span class="text-sm text-n-slate-12">
            {{ t('CALENDAR.DIALOG.REMINDER.CHECKBOX') }}
          </span>
        </label>
        <label v-if="form.reminderEnabled" class="flex flex-col gap-1 pl-6">
          <span class="text-xs text-n-slate-10">
            {{ t('CALENDAR.DIALOG.FIELDS.REMIND') }}
          </span>
          <select v-model.number="form.reminderOffset" :class="fieldClass">
            <option
              v-for="opt in REMINDER_OFFSETS"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
          <span class="text-xs text-n-slate-10">
            {{ t('CALENDAR.DIALOG.REMINDER.HELPER') }}
          </span>
        </label>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          v-if="isEditing"
          :label="t('CALENDAR.DIALOG.ACTIONS.DELETE')"
          variant="link"
          color="ruby"
          class="h-10 hover:!no-underline"
          @click="handleRemove"
        />
        <span v-else />
        <div class="flex items-center gap-3">
          <Button
            :label="t('CALENDAR.DIALOG.ACTIONS.CANCEL')"
            variant="link"
            type="reset"
            class="h-10 hover:!no-underline hover:text-n-brand"
            @click="dialogRef?.close()"
          />
          <Button
            :label="
              isEditing
                ? t('CALENDAR.DIALOG.ACTIONS.SAVE')
                : t('CALENDAR.DIALOG.ACTIONS.CREATE')
            "
            color="blue"
            type="button"
            class="h-10"
            :disabled="isFormInvalid"
            @click="handleConfirm"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
